// Handcrafted SVG assets for Chicken Road — modern, elegant, no external deps

export function ChickenSvg({ size = 40, variant = "idle" }) {
  // Front-ish view, playful but refined chicken
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className={variant === "hit" ? "opacity-70" : ""} aria-hidden>
      <defs>
        <linearGradient id="chBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E5E5E5" />
        </linearGradient>
        <radialGradient id="chBelly" cx="0.5" cy="0.6" r="0.5">
          <stop offset="0%" stopColor="#FFFDF5" />
          <stop offset="100%" stopColor="#EFE7D0" />
        </radialGradient>
      </defs>

      {/* Feet */}
      <path d="M24 55 L22 60 M24 55 L26 60 M24 55 L20 60" stroke="#E2B96A" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M40 55 L38 60 M40 55 L42 60 M40 55 L36 60" stroke="#E2B96A" strokeWidth="1.8" strokeLinecap="round" />

      {/* Body */}
      <ellipse cx="32" cy="42" rx="16" ry="14" fill="url(#chBody)" stroke="#0B0D10" strokeWidth="1" />
      <ellipse cx="32" cy="46" rx="10" ry="8" fill="url(#chBelly)" opacity="0.9" />

      {/* Wing hint */}
      <path d="M20 42 Q26 48 30 44" fill="none" stroke="#CBCBCB" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M44 42 Q38 48 34 44" fill="none" stroke="#CBCBCB" strokeWidth="1.2" strokeLinecap="round" />

      {/* Head */}
      <circle cx="32" cy="24" r="12" fill="url(#chBody)" stroke="#0B0D10" strokeWidth="1" />

      {/* Comb */}
      <path d="M26 14 Q28 8 30 14 Q32 8 34 14 Q36 8 38 14 L38 18 Q32 20 26 18 Z" fill="#EF4444" stroke="#B91C1C" strokeWidth="0.6" />

      {/* Wattle */}
      <path d="M30 32 Q32 36 34 32 Q34 34 32 35 Q30 34 30 32 Z" fill="#EF4444" />

      {/* Beak */}
      <path d="M28 26 L22 28 L28 30 Z" fill="#F5A623" stroke="#B4740E" strokeWidth="0.5" />

      {/* Eye */}
      <circle cx="36" cy="22" r="2.6" fill="#FFFFFF" stroke="#0B0D10" strokeWidth="0.7" />
      <circle cx="36.6" cy="22.4" r="1.3" fill="#0B0D10" />
      <circle cx="37" cy="21.9" r="0.4" fill="#FFFFFF" />
    </svg>
  );
}

const CAR_PALETTES = [
  { body: "#EF4444", roof: "#B91C1C", glass: "#0B0D10" },  // red
  { body: "#0EA5E9", roof: "#0369A1", glass: "#0B0D10" },  // sky
  { body: "#22C55E", roof: "#15803D", glass: "#0B0D10" },  // green
  { body: "#F59E0B", roof: "#B45309", glass: "#0B0D10" },  // amber
  { body: "#E5E7EB", roof: "#9CA3AF", glass: "#0B0D10" },  // silver
];

export function CarSvg({ width = 28, palette }) {
  // Deterministic-random palette if not passed
  const p = palette || CAR_PALETTES[Math.floor(Math.random() * CAR_PALETTES.length)];
  const h = width * 1.9;
  return (
    <svg width={width} height={h} viewBox="0 0 60 120" aria-hidden>
      <defs>
        <linearGradient id={`carBody-${p.body.slice(1)}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={p.roof} />
          <stop offset="50%" stopColor={p.body} />
          <stop offset="100%" stopColor={p.roof} />
        </linearGradient>
      </defs>
      {/* Shadow */}
      <rect x="6" y="14" width="48" height="96" rx="14" fill="rgba(0,0,0,0.35)" />
      {/* Body */}
      <rect x="6" y="10" width="48" height="96" rx="14" fill={`url(#carBody-${p.body.slice(1)})`} stroke="#0B0D10" strokeWidth="1" />
      {/* Headlights (front) */}
      <rect x="10" y="12" width="8" height="4" rx="1" fill="#FFF8C5" opacity="0.95" />
      <rect x="42" y="12" width="8" height="4" rx="1" fill="#FFF8C5" opacity="0.95" />
      {/* Front windshield */}
      <path d="M12 22 L48 22 L44 38 L16 38 Z" fill={p.glass} opacity="0.85" />
      {/* Roof panel */}
      <rect x="16" y="40" width="28" height="34" rx="4" fill="#0B0D10" opacity="0.55" />
      {/* Rear windshield */}
      <path d="M16 76 L44 76 L48 94 L12 94 Z" fill={p.glass} opacity="0.85" />
      {/* Taillights */}
      <rect x="10" y="100" width="8" height="4" rx="1" fill="#B91C1C" />
      <rect x="42" y="100" width="8" height="4" rx="1" fill="#B91C1C" />
      {/* Side mirrors */}
      <rect x="3" y="30" width="4" height="6" rx="1.5" fill={p.roof} stroke="#0B0D10" strokeWidth="0.4" />
      <rect x="53" y="30" width="4" height="6" rx="1.5" fill={p.roof} stroke="#0B0D10" strokeWidth="0.4" />
      {/* Center highlight (subtle sheen) */}
      <rect x="26" y="10" width="8" height="96" fill="rgba(255,255,255,0.06)" />
    </svg>
  );
}

export function LaneStripes() {
  return (
    <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 opacity-40"
      style={{background: "repeating-linear-gradient(90deg,#FFFFFF 0 8px,transparent 8px 20px)"}} />
  );
}

export function AsphaltBg() {
  return (
    <div className="absolute inset-0 pointer-events-none"
      style={{background: "radial-gradient(120% 60% at 50% 100%, rgba(255,255,255,0.02), transparent 60%)"}} />
  );
}

export { CAR_PALETTES };
